import { createElement, memo, PropsWithChildren, useState } from 'react';
import { useDesignTokens, renderInlineCssVars } from '../../../theme';
import { useSettingsReset } from '../store/SettingsStore';
import { Button, ButtonVariant } from '../../Button';
import { Heading } from '../../Heading';
import { useMessageDialogText, MessageDialog } from '../../MessageDialog';
import { Flow } from '../../Layouts/Flow';
import { TestIdProps, postfixTestId } from '../../../util/testId';
import bundle from '../NovaComponents-Settings-gui-icu.properties';
import { FormGroup } from '../model/FormGroup';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';

import cn from './FormGroupNavigation.css.json';
import './FormGroupNavigation.css';

const useTextLocalizer = createUseTextLocalizer(bundle);

export interface FormGroupWrapperProps extends TestIdProps {
  heading: string;
  id: string;
}

/**
 * HOC used to create a connected Form to be set upon
 * to the FormGroup model object for Settings component.
 */
export function connectToPreferenceStore(
  Form: (props: PropsWithChildren<Record<string, unknown>>) => JSX.Element,
  dependentPreferenceKeys: Array<string>,
  hideReset = false
): FormGroup['form'] {
  const MemoizedForm = memo(Form);

  const FormGroupWrapper = ({ heading, id, ...testIdProps }: FormGroupWrapperProps) => {
    const [isResetDialogOpen, setResetDialogOpen] = useState<boolean>(false);

    const localize = useTextLocalizer();
    const { RESET, CANCEL } = useMessageDialogText();

    const [isResetButtonEnabled, reset] = useSettingsReset(dependentPreferenceKeys);

    const dt = useDesignTokens();
    const tokens = renderInlineCssVars(
      { 'font-weight-thick': dt.fontNova.weight.thick },
      'form-group-navigation'
    );

    return (
      <Flow orientation="vertical" style={tokens} className={cn.container}>
        <header className={cn.header}>
          <Heading level={5} wrapText={false} className={cn.headerTitle} id={`${id}-header`}>
            {heading}
          </Heading>
          {!hideReset && (
            <Button
              testId={postfixTestId(testIdProps.testId, 'resetButton')}
              text={localize('settings.reset.button.label')}
              className={cn.resetButton}
              variant={ButtonVariant.LightSmall}
              disabled={!isResetButtonEnabled}
              onPress={() => setResetDialogOpen(true)}
            />
          )}
        </header>
        <section className={cn.content} tabIndex={-1}>
          <MemoizedForm />
        </section>
        <MessageDialog
          testId="settings-resetAction"
          type="warning"
          isOpen={isResetDialogOpen}
          title={localize('settings.reset.dialog.title')}
          text={localize('settings.reset.warning.msg')}
          firstAction={{
            text: RESET,
            onPress: () => {
              setResetDialogOpen(false);
              reset();
            },
          }}
          dismissAction={{
            text: CANCEL,
            onPress: () => setResetDialogOpen(false),
            isDefault: true,
          }}
        />
      </Flow>
    );
  };

  FormGroupWrapper.displayName = `FormGroupWrapper(${Form.name})`;

  return FormGroupWrapper;
}
